/**
 * useDocumentManagement Hook
 * Manages document state and operations
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { apiService, type Document as ApiDocument } from '../services/api';
import logger from '../utils/simpleLogger';
import { authManager } from '../utils/authManager';

interface UseDocumentManagementReturn {
  // State
  documents: ApiDocument[];
  selectedDocuments: string[];
  isLoading: boolean;
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;

  // Actions
  loadDocuments: (projectId?: string) => Promise<void>; // projectId ahora opcional
  selectDocument: (documentId: string) => void;
  deselectDocument: (documentId: string) => void;
  toggleDocument: (documentId: string) => void;
  selectAllDocuments: () => void;
  deselectAllDocuments: () => void;
  setSelectedDocuments: (documentIds: string[]) => void;
  uploadDocument: (file: File, projectId?: string) => Promise<ApiDocument | null>; // projectId ahora opcional
  deleteDocument: (documentId: string) => Promise<boolean>;
  clearError: () => void;
  ensureSelection: () => void; // Nueva función para auto-selección
  updateDocumentStatus: (
    documentId: string,
    status: 'pending' | 'processing' | 'completed' | 'failed'
  ) => void;
}

export function useDocumentManagement(): UseDocumentManagementReturn {
  const [documents, setDocuments] = useState<ApiDocument[]>([]);
  const [selectedDocuments, setSelectedDocumentsState] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const activeEventSourcesRef = useRef<Map<string, EventSource>>(new Map());

  /**
   * Load documents for a project (or all user documents if no projectId)
   */
  const loadDocuments = useCallback(async (projectId?: string) => {
    setIsLoading(true);
    setError(null);
    logger.info('📄 [Documents] loadDocuments() called', { projectId: projectId || 'ALL' });

    try {
      const response = await apiService.getDocuments();
      if (response.documents) {
        setDocuments(response.documents);
        logger.info('✅ [Documents] Documents loaded', {
          projectId: projectId || 'ALL',
          count: response.documents.length,
          documentIds: response.documents.map((doc) => doc.id),
        });
      } else {
        throw new Error('No documents returned from API');
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Error loading documents';
      setError(errorMsg);
      logger.error('Failed to load documents', err, 'Documents');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Select a document
   */
  const selectDocument = useCallback((documentId: string) => {
    logger.info('📄 [Documents] selectDocument() called', { documentId });
    setSelectedDocumentsState((prev) => {
      if (!prev.includes(documentId)) {
        logger.info('✅ [Documents] Document selected', {
          documentId,
          totalSelected: prev.length + 1,
        });
        return [...prev, documentId];
      }
      return prev;
    });
  }, []);

  /**
   * Deselect a document
   */
  const deselectDocument = useCallback((documentId: string) => {
    logger.info('📄 [Documents] deselectDocument() called', { documentId });
    setSelectedDocumentsState((prev) => {
      const newSelection = prev.filter((id) => id !== documentId);
      if (newSelection.length !== prev.length) {
        logger.info('⚙️ [Documents] Document deselected', {
          documentId,
          remaining: newSelection.length,
        });
      }
      return newSelection;
    });
  }, []);

  /**
   * Toggle document selection
   */
  const toggleDocument = useCallback((documentId: string) => {
    logger.info('📄 [Documents] toggleDocument() called', { documentId });
    setSelectedDocumentsState((prev) => {
      if (prev.includes(documentId)) {
        logger.info('⚙️ [Documents] Document deselected', {
          documentId,
          remaining: prev.length - 1,
        });
        return prev.filter((id) => id !== documentId);
      } else {
        logger.info('✅ [Documents] Document selected', {
          documentId,
          totalSelected: prev.length + 1,
        });
        return [...prev, documentId];
      }
    });
  }, []);

  /**
   * Select all documents
   */
  const selectAllDocuments = useCallback(() => {
    const allIds = documents.map((doc) => doc.id);
    setSelectedDocumentsState(allIds);
    logger.info('✅ [Documents] Selected all documents', {
      total: allIds.length,
    });
  }, [documents]);

  /**
   * Deselect all documents
   */
  const deselectAllDocuments = useCallback(() => {
    setSelectedDocumentsState([]);
    logger.info('⚙️ [Documents] Deselected all documents');
  }, []);

  /**
   * Set selected documents directly
   */
  const setSelectedDocuments = useCallback((documentIds: string[]) => {
    setSelectedDocumentsState(documentIds);
    logger.info('📄 [Documents] setSelectedDocuments()', {
      count: documentIds.length,
      documentIds,
    });
  }, []);

  /**
   * Subscribe to document processing SSE for real-time status updates.
   * Auto-selects the document only when processing completes.
   */
  const subscribeToProcessingStatus = useCallback((documentId: string) => {
    // Close existing subscription if any
    const existing = activeEventSourcesRef.current.get(documentId);
    if (existing) existing.close();

    const token = authManager.getToken();
    const baseUrl =
      import.meta.env.VITE_API_BASE_URL ||
      (import.meta.env.DEV ? 'http://localhost:8001/api' : '/api');
    const url = `${baseUrl.replace(/\/$/, '')}/documents/${documentId}/status-stream${
      token ? `?token=${token}` : ''
    }`;

    const es = new EventSource(url);
    activeEventSourcesRef.current.set(documentId, es);

    es.addEventListener('status_update', (event) => {
      try {
        const data = JSON.parse(event.data);
        logger.info('📡 [Documents] SSE status_update', {
          documentId,
          status: data.processing_status,
        });
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === documentId ? { ...doc, processing_status: data.processing_status } : doc
          )
        );
      } catch {
        /* ignore parse errors */
      }
    });

    es.addEventListener('processing_complete', (event) => {
      try {
        const data = JSON.parse(event.data);
        logger.info('✅ [Documents] SSE processing_complete', {
          documentId,
          status: data.processing_status,
        });
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === documentId ? { ...doc, processing_status: data.processing_status } : doc
          )
        );
        // Auto-select NOW that processing is done
        if (data.processing_status === 'completed') {
          setSelectedDocumentsState((prev) => {
            if (!prev.includes(documentId)) {
              logger.info('✅ [Documents] Auto-selected after processing complete', { documentId });
              return [...prev, documentId];
            }
            return prev;
          });
        }
      } catch {
        /* ignore parse errors */
      }
      es.close();
      activeEventSourcesRef.current.delete(documentId);
    });

    es.addEventListener('error', () => {
      logger.warn('📡 [Documents] SSE error for', { documentId });
      es.close();
      activeEventSourcesRef.current.delete(documentId);
    });

    es.addEventListener('timeout', () => {
      logger.warn('📡 [Documents] SSE timeout for', { documentId });
      es.close();
      activeEventSourcesRef.current.delete(documentId);
    });
  }, []);

  /**
   * Upload a document (projectId optional - will use default project if not provided)
   */
  const uploadDocument = useCallback(
    async (file: File, projectId?: string): Promise<ApiDocument | null> => {
      setIsUploading(true);
      setUploadProgress(0);
      setError(null);

      try {
        logger.info('📄 [Documents] uploadDocument() called', {
          fileName: file.name,
          fileSize: file.size,
          projectId: projectId || 'DEFAULT',
        });

        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            const next = Math.min(prev + 10, 90);
            logger.debug('📄 [Documents] upload progress', {
              percent: next,
              fileName: file.name,
            });
            return next;
          });
        }, 200);

        const response = await apiService.uploadDocument(file);

        clearInterval(progressInterval);
        setUploadProgress(100);

        if (response.document) {
          const newDoc = response.document;
          // Add document to list with pending status (do NOT auto-select yet)
          setDocuments((prev) => [
            ...prev,
            { ...newDoc, processing_status: newDoc.processing_status || 'pending' },
          ]);

          logger.info('✅ [Documents] Document uploaded, subscribing to processing status', {
            documentId: newDoc.id,
            fileName: file.name,
            processingStatus: newDoc.processing_status,
          });

          // Subscribe to real-time processing updates
          // Auto-selection will happen on processing_complete event
          if (newDoc.processing_status !== 'completed') {
            subscribeToProcessingStatus(newDoc.id);
          } else {
            // Already completed (e.g., duplicate or instant processing)
            setSelectedDocumentsState((prev) => {
              if (!prev.includes(newDoc.id)) return [...prev, newDoc.id];
              return prev;
            });
          }

          setTimeout(() => {
            setIsUploading(false);
            setUploadProgress(0);
          }, 500);

          return newDoc;
        } else {
          throw new Error('No document returned from API');
        }
      } catch (err: any) {
        const errorMsg = err.message || 'Error uploading document';
        setError(errorMsg);
        logger.error('❌ [Documents] uploadDocument error', {
          message: errorMsg,
          fileName: file.name,
          stack: err.stack,
        });
        setIsUploading(false);
        setUploadProgress(0);
        return null;
      }
    },
    [subscribeToProcessingStatus]
  );

  /**
   * Update document status locally (for immediate UI feedback)
   */
  const updateDocumentStatus = useCallback(
    (documentId: string, status: 'pending' | 'processing' | 'completed' | 'failed') => {
      setDocuments((prev) =>
        prev.map((doc) => (doc.id === documentId ? { ...doc, processing_status: status } : doc))
      );
    },
    []
  );

  /**
   * Delete a document
   */
  const deleteDocument = useCallback(async (documentId: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      logger.info('📄 [Documents] deleteDocument() called', { documentId });
      const response = await apiService.deleteDocument(documentId);
      // Delete returns { message: string }, success if no error thrown
      setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      setSelectedDocumentsState((prev) => prev.filter((id) => id !== documentId));
      logger.info('✅ [Documents] Document deleted', { documentId });
      return true;
    } catch (err: any) {
      const errorMsg = err.message || 'Error deleting document';
      setError(errorMsg);
      logger.error('Failed to delete document', err, 'Documents');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * 🎯 AUTO-SELECCIÓN: Asegurar que haya documentos seleccionados
   * Si no hay documentos seleccionados pero hay documentos disponibles,
   * selecciona todos automáticamente
   */
  const ensureSelection = useCallback(() => {
    if (selectedDocuments.length === 0 && documents.length > 0) {
      const allIds = documents.map((d) => d.id);
      setSelectedDocumentsState(allIds);
      logger.info('✅ [Documents] Auto-selected all documents (none were selected)', {
        count: allIds.length,
      });
    }
  }, [selectedDocuments.length, documents]);

  return {
    // State
    documents,
    selectedDocuments,
    isLoading,
    isUploading,
    uploadProgress,
    error,

    // Actions
    loadDocuments,
    selectDocument,
    deselectDocument,
    toggleDocument,
    selectAllDocuments,
    deselectAllDocuments,
    setSelectedDocuments,
    uploadDocument,
    deleteDocument,
    clearError,
    ensureSelection, // Nueva función
    updateDocumentStatus,
  };
}
